import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { lookupGeo } from "@/lib/geoip";
import { isTrustedCc, isCcInCooldown, startCcCooldown, trustCc } from "@/lib/sessionTrust";

const KEY = "bloq_login_alert_seen";

function loadSeen() {
  try {
    const v = localStorage.getItem(KEY);
    const parsed = v ? JSON.parse(v) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
function saveSeen(ids) {
  try { localStorage.setItem(KEY, JSON.stringify([...new Set(ids)])); } catch { /* ignore */ }
}

// App-wide watcher: polls active sessions and pops a security heads-up toast the moment a NEW
// sign-in appears from a different country than the current device — so users don't have to open
// Settings to notice it. First observation records the baseline (existing sessions never alert).
// Client-side detection only (the backend is external, no server push): fires when the poll picks
// up a new foreign session while the app is open, or on next app open after one lands.
export function NewLoginWatcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const processedRef = useRef(new Set());

  const { data } = useQuery({
    queryKey: ["auth-sessions"],
    queryFn: api.listSessions,
    enabled: !!user,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  useEffect(() => {
    const sessions = data?.sessions || [];
    if (!sessions.length) return;

    const allIds = sessions.map((s) => s.id);
    const seen = loadSeen();
    if (seen === null) {
      saveSeen(allIds); // baseline — don't alert on pre-existing sessions
      return;
    }

    const seenSet = new Set(seen);
    const fresh = sessions.filter(
      (s) => !s.current && !seenSet.has(s.id) && !processedRef.current.has(s.id),
    );
    if (!fresh.length) {
      saveSeen([...seen, ...allIds]);
      return;
    }
    fresh.forEach((s) => processedRef.current.add(s.id));

    (async () => {
      const cur = sessions.find((s) => s.current);
      const curGeo = cur ? await lookupGeo(cur.ip) : null;
      const curCc = curGeo?.cc || null;

      const foreign = [];
      if (curCc) {
        for (const s of fresh) {
          const g = await lookupGeo(s.ip);
          // Skip locations the user trusts or that already alerted recently (cooldown).
          if (g?.cc && g.cc !== curCc && !isTrustedCc(g.cc) && !isCcInCooldown(g.cc)) {
            foreign.push({ s, g });
          }
        }
      }

      // Mark every current session as accounted for so we never re-alert on the same login.
      saveSeen([...seen, ...allIds]);

      if (!foreign.length) return;
      const ccs = [...new Set(foreign.map((f) => f.g.cc))];
      ccs.forEach(startCcCooldown); // stay quiet for this location for a while
      const label =
        foreign.length === 1
          ? `New sign-in from ${foreign[0].g.label}`
          : `${foreign.length} new sign-ins from unfamiliar locations`;
      toast.warning(label, {
        description: "This is a different country than this device. If it wasn't you, secure your account.",
        icon: <MapPin className="h-4 w-4 text-amber-400" />,
        duration: 15000,
        action: { label: "Review", onClick: () => navigate("/app/settings") },
        cancel: { label: "This was me", onClick: () => ccs.forEach(trustCc) },
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return null;
}
